import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import OtpInput from "react-otp-input";
import { Loader2, AlertCircle, CheckCircle, X } from "lucide-react";
import { query, collection, where, getDocs, updateDoc, doc } from "firebase/firestore";
import { dbMobile } from "../../../config/firebaseConfig";
import dbService from "../../../firebase/dbService";
import envConfig from "../../../config/envConfig";
import { updateUserDetails } from "../../../redux/features/authSlice";
import { Button, TextInput } from "../../../components";
import { FaWhatsapp } from "react-icons/fa";

const VerifyPhoneModal = ({ parentsDetails, onClose }) => {
  const dispatch = useDispatch();
  const [phone, setPhone] = useState(parentsDetails?.phone || "");
  const [otp, setOtp] = useState("");
  const [showOtp, setShowOtp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [timer, setTimer] = useState(0);
  const [role, setRole] = useState(parentsDetails?.accountType || "parent");

  useEffect(() => {
    if (parentsDetails?.accountType) {
      setRole(parentsDetails.accountType);
    }
  }, [parentsDetails?.accountType]);

  // Timer cooldown logic
  useEffect(() => {
    let interval = null;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handlePhoneChange = (e) => {
    setError("");
    const value = e.target.value.replace(/\D/g, ""); // Allow digits only
    if (value.length <= 10) {
      setPhone(value);
    }
  };

  const handleSendOtp = async (e) => {
    e?.preventDefault();
    if (!phone || phone.length !== 10) {
      setError("Please enter a valid 10-digit phone number.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${envConfig.backendUrl}/generateWhatsappOtpWebV1`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phoneNumber: phone,
            email: parentsDetails?.email,
            purpose: "Verification",
          }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Failed to send OTP.");
      }

      setShowOtp(true);
      setTimer(60);
    } catch (err) {
      console.error(err);
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e?.preventDefault();
    if (!otp || otp.length !== 6) {
      setError("Please enter the 6-digit OTP code.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${envConfig.backendUrl}/verifyWhatsappOtpWebV1`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phoneNumber: phone,
            otp: otp,
            uid: parentsDetails?.uid || parentsDetails?.id,
          }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Invalid OTP.");
      }

      // OTP Verified! Let's update databases
      setSuccess(true);

      // 1. Update master 'user' document
      const uid = parentsDetails?.uid || parentsDetails?.id;
      const userUpdate = { phone, phoneVerified: true, accountType: role };
      if (parentsDetails?.phone && parentsDetails.phone !== phone) {
        const currentOldPhones = parentsDetails?.old_phone || [];
        if (!currentOldPhones.includes(parentsDetails.phone)) {
          userUpdate.old_phone = [...currentOldPhones, parentsDetails.phone];
        }
      }
      await dbService.updateDocument("user", userUpdate, uid);

      // 2. Update role-specific document
      const newRoleCollection = role === "student" ? "students" : "parents";
      const oldRoleCollection = parentsDetails?.accountType === "student" ? "students" : "parents";

      // If role changed, delete the document from the old collection
      if (parentsDetails?.accountType && parentsDetails.accountType !== role) {
        await dbService.deleteDocument(oldRoleCollection, uid).catch(() => null);
      }

      // Save/Update in the correct role collection
      const roleDocData = {
        uid,
        email: parentsDetails?.email || "",
        name: parentsDetails?.name || parentsDetails?.displayName || "",
        phone,
        phoneVerified: true,
      };
      if (parentsDetails?.phone && parentsDetails.phone !== phone) {
        const currentOldPhones = parentsDetails?.old_phone || [];
        if (!currentOldPhones.includes(parentsDetails.phone)) {
          roleDocData.old_phone = [...currentOldPhones, parentsDetails.phone];
        }
      }
      await dbService.createDocumentWithId(newRoleCollection, roleDocData, uid);

      // Trigger Welcome WhatsApp Message
      try {
        await fetch(`${envConfig.backendUrl}/sendWhatsAppNotificationV1`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phoneNumber: phone,
            templateType: "welcome",
            parameters: [parentsDetails?.name || parentsDetails?.displayName || "User"]
          })
        });
      } catch (e) {
        console.error("Failed to send welcome message:", e);
      }

      // 3. Update Redux store to close modal
      setTimeout(() => {
        const reduxUpdate = { phone, phoneVerified: true, accountType: role };
        if (parentsDetails?.phone && parentsDetails.phone !== phone) {
          const currentOldPhones = parentsDetails?.old_phone || [];
          if (!currentOldPhones.includes(parentsDetails.phone)) {
            reduxUpdate.old_phone = [...currentOldPhones, parentsDetails.phone];
          }
        }
        dispatch(updateUserDetails(reduxUpdate));
      }, 1500);

    } catch (err) {
      console.error(err);
      setError(err.message || "Incorrect OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    setShowOtp(false);
    setOtp("");
    setError("");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md p-4 animate-fade-in">
      <div className="relative w-full max-w-md bg-white rounded-2xl p-6 sm:p-8 shadow-2xl border border-gray-100 flex flex-col gap-5 animate-scale-up">
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-450 hover:text-gray-650 transition-colors"
            type="button"
          >
            <X className="w-5 h-5" />
          </button>
        )}
        {success ? (
          <div className="flex flex-col items-center justify-center text-center py-6 gap-4">
            <CheckCircle className="w-16 h-16 text-emerald-500 animate-bounce" />
            <h3 className="text-xl font-extrabold text-gray-900">
              Verification Successful!
            </h3>
            <p className="text-sm text-gray-600 font-medium leading-relaxed max-w-xs">
              Your phone number has been verified. Redirecting you to your dashboard...
            </p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex flex-col items-center text-center gap-3">
              <FaWhatsapp className="w-12 h-12 text-[#25D366]" />
              <h3 className="text-xl sm:text-2xl font-extrabold text-gray-900 tracking-tight">
                Verify Your WhatsApp Number
              </h3>
              <p className="text-sm text-gray-600 font-medium leading-relaxed">
                To receive updates, test reports, and exam alerts directly on WhatsApp, please verify your number.
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-start gap-2 bg-red-50 text-red-700 p-3 rounded-lg border border-red-100 text-xs font-semibold">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Step 1: Input Phone */}
            {!showOtp ? (
              <form onSubmit={handleSendOtp} className="flex flex-col gap-4">

                <div className="flex flex-col gap-2">
                  <label className="text-xs font-bold text-gray-700 tracking-wide">
                    WhatsApp Number
                  </label>
                  <TextInput
                    type="tel"
                    placeholderText="Enter 10-digit number"
                    inputName="phone-number"
                    inputValue={phone}
                    handleChange={handlePhoneChange}
                    isRequired={true}
                  />
                </div>

                <div className="mt-2">
                  <Button
                    version="secondary"
                    text={loading ? "Sending..." : "Send OTP"}
                    type="submit"
                    loading={loading}
                    isDisabled={phone.length !== 10 || loading}
                  />
                </div>
              </form>
            ) : (
              /* Step 2: Input OTP */
              <form onSubmit={handleVerifyOtp} className="flex flex-col gap-4">
                <div className="flex flex-col gap-3">
                  <p className="text-xs text-gray-600 font-medium">
                    Please enter the code sent to <strong>+91 *******{phone.slice(-4)}</strong>
                  </p>
                  
                  <div className="flex justify-center py-2 otp-main-card">
                    <OtpInput
                      value={otp}
                      onChange={setOtp}
                      numInputs={6}
                      renderSeparator={<span className="w-2"></span>}
                      shouldAutoFocus
                      renderInput={(props) => (
                        <input
                          {...props}
                          style={{ width: "3rem" }}
                          className="h-12 border-2 border-gray-300 rounded-lg text-center text-lg font-bold focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all"
                        />
                      )}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2.5 mt-2">
                  <Button
                    version="secondary"
                    text={loading ? "Verifying..." : "Verify OTP"}
                    type="submit"
                    loading={loading}
                    isDisabled={otp.length !== 6 || loading}
                  />
                  
                  <div className="flex justify-between items-center text-xs mt-1">
                    <button
                      type="button"
                      onClick={handleGoBack}
                      className="text-gray-500 hover:text-gray-700 font-semibold transition-colors"
                    >
                      Change Phone Number
                    </button>
                    
                    <div>
                      {timer > 0 ? (
                        <span className="text-gray-400 font-semibold">
                          Resend in {timer}s
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={handleSendOtp}
                          className="text-blue-600 hover:text-blue-800 font-bold transition-colors"
                        >
                          Resend Code
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyPhoneModal;
